/**
 * 标签组件
 *
 * 用于表单字段的标签显示
 * 与 React Hook Form 无缝集成
 */
import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'

/* ============================================
 * 标签变体定义
 * ============================================ */

const labelVariants = cva(
  // 基础样式
  'text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70'
)

/* ============================================
 * 类型定义
 * ============================================ */

/** 标签组件属性 */
export interface LabelProps
  extends React.LabelHTMLAttributes<HTMLLabelElement>,
    VariantProps<typeof labelVariants> {
  /** 必填标记 */
  required?: boolean
}

/* ============================================
 * 组件实现
 * ============================================ */

/**
 * 标签组件
 *
 * @example
 * // 基础用法
 * <Label htmlFor="email">邮箱</Label>
 *
 * @example
 * // 必填字段
 * <Label htmlFor="email" required>邮箱</Label>
 *
 * @example
 * // 自定义样式
 * <Label className="text-lg">大号标签</Label>
 */
const Label = React.forwardRef<HTMLLabelElement, LabelProps>(
  ({ className, required, children, ...props }, ref) => {
    return (
      <label
        ref={ref}
        className={cn(labelVariants(), className)}
        {...props}
      >
        {children}
        {/* 必填标记 */}
        {required && (
          <span className="text-destructive ml-1" aria-hidden="true">
            *
          </span>
        )}
      </label>
    )
  }
)

Label.displayName = 'Label'

/* ============================================
 * 导出
 * ============================================ */

export { Label }
export default Label